import React from 'react';
import { useCurrentuser } from '@uplift/contexts';
import { usePatientMatchedProvider } from '@uplift/queries';
import { usePatientDetails } from '../PatientDetails.hooks';
import { ProviderMedicalRole } from '@uplift/types';
import { usePsychiatryMatching } from '../../../../../hooks';

export type UsePsychiatryReferralFlowArgs = {
  patientId: string;
};

export const usePsychiatryReferralFlow = (args: UsePsychiatryReferralFlowArgs) => {
  const { activeMatches = [], insurances = [], userProfile } = usePatientDetails(args.patientId);
  const me = useCurrentuser();
  const { data: matchedProviderData } = usePatientMatchedProvider(args.patientId, me.id);
  const insuranceCompanyId = insurances.find(ins => !ins.insurance.isSecondaryInsurance)?.insurance?.companyId;
  const stateId = userProfile.address?.stateId;

  const hasActivePsychMatch = React.useMemo(() => {
    if (activeMatches.find(m =>
      m.match.state === 'active'
          && m.provider.medicalRole === ProviderMedicalRole.psychiatrist
    )) return true;
    return false;
  }, [ activeMatches ]);

  const isPsychiatryMatchingAllowed = React.useMemo(() => {
    if (matchedProviderData?.match.state === 'active') {
      if (me.medicalRole === ProviderMedicalRole.psychiatrist) return false;
      if (hasActivePsychMatch) return false;

      return true;
    }

    return false;
  }, [ hasActivePsychMatch, matchedProviderData?.match.state, me.medicalRole ]);

  const matchingStateId = isPsychiatryMatchingAllowed && stateId;
  const {
    matchingFetching,
    matchingProvidersType,
    psychiatryData = [],
  } = usePsychiatryMatching({
    stateId: matchingStateId,
    insuranceCompanyUuid: insuranceCompanyId,
    patientId: args.patientId,
  });

  const matchingFlowContainerAvailable = React.useMemo(() => {
    if (!isPsychiatryMatchingAllowed || matchingFetching) {
      return false;
    }

    return true;
  }, [ isPsychiatryMatchingAllowed, matchingFetching ]);

  return {
    stateId: matchingStateId,
    hasActivePsychMatch,
    isPsychiatryMatchingAllowed,
    psychiatryData,
    matchingFetching,
    matchingFlowContainerAvailable,
    matchingProvidersType,
  };
};
